/** PROBATION-REVIEW-001 P5 — compact HR Dashboard widget (one summary call, no polling). */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { CalendarClock, AlertTriangle, ClipboardCheck, Repeat, ChevronRight, UserX, ShieldAlert, Link2Off, Info } from 'lucide-react';
import { formatDateOnly } from '../../../../utils/formatters';
import { API, h } from './probationApi';
import './probation.css';

const TILES = [
  { bucket: 'due_14', label: 'Due ≤ 14 days', tone: 'amber', icon: CalendarClock, sub: 'Review window open' },
  { bucket: 'overdue', label: 'Overdue', tone: 'rose', icon: AlertTriangle, sub: 'Past end date' },
  { bucket: 'awaiting_hr', label: 'Awaiting HR', tone: 'violet', icon: ClipboardCheck, sub: 'Recommendation submitted' },
  { bucket: 'extended', label: 'Extended', tone: 'indigo', icon: Repeat, sub: 'Active extensions' },
];

const REASONS = {
  not_verified: { label: 'Pending HR verification', icon: ShieldAlert, cls: 'prb-violet' },
  reviewer_missing: { label: 'No Reporting Manager', icon: UserX, cls: 'prb-rose' },
  reviewer_self: { label: 'Manager is self', icon: UserX, cls: 'prb-rose' },
  reviewer_not_found: { label: 'Manager not found', icon: UserX, cls: 'prb-rose' },
  reviewer_inactive: { label: 'Manager inactive', icon: UserX, cls: 'prb-amber' },
  reviewer_unlinked: { label: 'Manager has no login', icon: Link2Off, cls: 'prb-amber' },
};

const useCountUp = (value, duration = 600) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (typeof value !== 'number') return undefined;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) { setN(value); return undefined; }
    let raf; const t0 = performance.now();
    const tick = (t) => { const p = Math.min((t - t0) / duration, 1); setN(Math.round(value * (1 - Math.pow(1 - p, 3)))); if (p < 1) raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return typeof value === 'number' ? n : '—';
};

const Tile = ({ t, value, onClick, delay }) => {
  const n = useCountUp(value);
  return (
    <button type="button" onClick={onClick} data-testid={`prb-tile-${t.bucket}`} style={{ animationDelay: `${delay}ms` }}
      className={`prb-w-tile tone-${t.tone} prb-w-in`} aria-label={`${t.label}: ${value ?? 0}. Open filtered Employee Directory`}>
      <span className="prb-w-ico"><t.icon className="w-3.5 h-3.5" /></span>
      <span className="prb-w-num" data-testid={`prb-tile-${t.bucket}-count`}>{n}</span>
      <span className="prb-w-label">{t.label}</span>
      <span className="prb-w-sub">{t.sub}</span>
      <ChevronRight className="prb-w-chev w-3.5 h-3.5" />
    </button>
  );
};

const initials = (name) => (name || '?').split(' ').filter(Boolean).slice(0, 2).map(s => s[0]).join('').toUpperCase();

const AttentionRow = ({ a, onOpen, delay }) => (
  <button type="button" onClick={onOpen} data-testid={`prb-attention-${a.employee_id}`} style={{ animationDelay: `${delay}ms` }} className="prb-w-row prb-w-in">
    <span className="prb-avatar" style={{ height: 28, width: 28, fontSize: 10.5 }}>{initials(a.full_name)}</span>
    <span className="min-w-0 flex-1 text-left">
      <span className="block text-[13px] font-semibold text-slate-800 truncate">{a.full_name || a.employee_id}</span>
      <span className="block text-[11px] text-slate-500 truncate">{a.team || '—'} · ends {formatDateOnly(a.end_date)}</span>
    </span>
    <span className="flex items-center gap-1 flex-wrap justify-end">
      {(a.reasons || []).slice(0, 2).map(r => {
        const m = REASONS[r] || { label: r.replace(/_/g, ' '), icon: ShieldAlert, cls: 'prb-slate' };
        return <span key={r} className={`prb-pill ${m.cls}`} style={{ height: 20, fontSize: 10.5 }}><m.icon className="w-3 h-3" />{m.label}</span>;
      })}
    </span>
    <ChevronRight className="w-3.5 h-3.5 text-slate-300 shrink-0" />
  </button>
);

export const ProbationReviewsWidget = ({ onSelectBucket }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);
  const lastFetch = useRef(0);

  const load = useCallback(async () => {
    try {
      lastFetch.current = Date.now();
      const r = await axios.get(`${API}/api/hr/probation/summary`, { headers: h() });
      setData(r.data);
    } catch (e) {
      if ([403, 404].includes(e?.response?.status)) setHidden(true);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  // Refresh only when the tab regains focus after ≥ 60 s — never on a timer.
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === 'visible' && Date.now() - lastFetch.current > 60000) load(); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [load]);

  if (hidden) return null;

  const go = (bucket) => (onSelectBucket ? onSelectBucket(bucket) : navigate(`/admin/hr/employees?probation_status=${bucket}`));
  const attention = data?.attention || [];
  const more = (data?.attention_total || 0) - attention.length;

  return (
    <section className="prb-w" data-testid="probation-reviews-widget" aria-busy={!data}>
      <header className="prb-w-head">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="prb-w-badge"><ClipboardCheck className="w-3.5 h-3.5" /></span>
          <div className="min-w-0">
            <h3 className="text-[13.5px] font-bold text-slate-900 tracking-tight leading-none">Probation Reviews</h3>
            <p className="text-[11px] text-slate-500 mt-1 truncate">
              {data ? <>{data.tracked} tracked · {data.in_progress} in progress · {data.confirmed} confirmed</> : 'Loading…'}
            </p>
          </div>
        </div>
        <button type="button" className="prb-w-link" data-testid="prb-view-all" onClick={() => go('tracked')}>View all <ChevronRight className="w-3 h-3" /></button>
      </header>

      <div className="prb-w-tiles">
        {TILES.map((t, i) => <Tile key={t.bucket} t={t} value={data ? data[t.bucket] : undefined} onClick={() => go(t.bucket)} delay={60 + i * 50} />)}
        <p className="prb-w-note" data-testid="prb-overlap-note"><Info className="w-3 h-3 shrink-0" />Awaiting HR and Extended can include the same employee — tiles are independent views, not additive.</p>
      </div>

      <div className="prb-w-att" data-testid="prb-attention-section">
        <div className="flex items-center justify-between gap-2 px-0.5">
          <span className="prb-metric-label">Needs attention{data ? ` · ${data.attention_total}` : ''}</span>
          {data && (data.pending_verification > 0 || data.reviewer_missing > 0) && (
            <span className="flex items-center gap-1.5">
              {data.pending_verification > 0 && <button type="button" className="prb-w-mini" data-testid="prb-mini-pending" onClick={() => go('pending_verification')}>Unverified {data.pending_verification}</button>}
              {data.reviewer_missing > 0 && <button type="button" className="prb-w-mini" data-testid="prb-mini-reviewer" onClick={() => go('reviewer_missing')}>Reviewer flagged {data.reviewer_missing}</button>}
            </span>
          )}
        </div>
        {!data ? (
          <div className="prb-w-skel" />
        ) : attention.length === 0 ? (
          <p className="prb-w-empty" data-testid="prb-attention-empty">Every tracked probation has a verified plan and a reachable Reporting Manager.</p>
        ) : (
          <div className="space-y-1.5">
            {attention.map((a, i) => <AttentionRow key={a.employee_id} a={a} delay={260 + i * 40} onOpen={() => navigate(`/admin/hr/employees/${a.employee_id}/edit?tab=probation`)} />)}
            {more > 0 && <button type="button" className="prb-w-link w-full justify-center" data-testid="prb-attention-more" onClick={() => go('pending_verification')}>+{more} more</button>}
          </div>
        )}
      </div>
    </section>
  );
};

export default ProbationReviewsWidget;
